import { useEffect, useMemo, useState } from 'react';
import { Check, Download } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import { useMySubscriptionStore } from '../../../store/useMySubscriptionStore';
import { formatDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/money';
import { SupportTicketType, TenantInvoiceStatus, type TenantInvoiceDto } from '../../../api/types/admin';
import { MeSupportCreateModal } from '../../Support/MeSupportCreateModal';

function isOverdue(inv: TenantInvoiceDto, today: Date): boolean {
  if (inv.status !== TenantInvoiceStatus.Issued) return false;
  if (!inv.dueDate) return false;
  return new Date(inv.dueDate).getTime() < today.getTime();
}

function statusColor(
  inv: TenantInvoiceDto,
  overdue: boolean,
): 'gray' | 'green' | 'red' | 'blue' {
  if (overdue) return 'red';
  if (inv.status === TenantInvoiceStatus.Paid) return 'green';
  if (inv.status === TenantInvoiceStatus.Cancelled) return 'gray';
  return 'blue';
}

function statusKey(inv: TenantInvoiceDto, overdue: boolean): string {
  if (overdue) return 'overdue';
  if (inv.status === TenantInvoiceStatus.Paid) return 'paid';
  if (inv.status === TenantInvoiceStatus.Cancelled) return 'cancelled';
  return 'issued';
}

export default function MySubscriptionPage() {
  const { t } = useTranslation(['subscription', 'common', 'support']);

  const { tenant, plan, invoices, loading, downloadingPdf, fetchTenant, fetchPlan, fetchInvoices, downloadPdf } =
    useMySubscriptionStore();

  const [switchModalOpen, setSwitchModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      await fetchTenant();
      await fetchPlan();
      await fetchInvoices();
    })();
  }, [fetchTenant, fetchPlan, fetchInvoices]);

  // fetchPlan needs tenant to be loaded first — if tenant arrives after a race, re-run.
  useEffect(() => {
    if (tenant && !plan) fetchPlan();
  }, [tenant, plan, fetchPlan]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
          {t('subscription:title')}
        </h1>
      </div>

      {/* Current Plan */}
      <Card title={t('subscription:currentPlan.title')}>
        {loading && !plan ? (
          <p className="text-[13px] text-slate-500">{t('common:actions.loading')}</p>
        ) : !plan ? (
          <p className="text-[13px] text-slate-500">{t('subscription:currentPlan.notAvailable')}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
              <h2 className="text-[20px] font-semibold tracking-tight text-slate-900">{plan.name}</h2>
              <div className="text-[20px] font-mono font-semibold text-slate-900 tabular-nums">
                {plan.price == null
                  ? t('subscription:currentPlan.onRequest')
                  : `${formatMoney(plan.price, { currency: plan.currency })} ${t('subscription:currentPlan.perMonth')}`}
              </div>
            </div>

            {plan.description && (
              <p className="text-[13px] text-slate-600">{plan.description}</p>
            )}

            {plan.features && plan.features.length > 0 && (
              <div>
                <div className="kicker mb-2">{t('subscription:currentPlan.included')}</div>
                <ul className="space-y-1.5">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px] text-slate-700">
                      <Check
                        className="w-3.5 h-3.5 mt-0.5 text-[#00A871] flex-shrink-0"
                        strokeWidth={3}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-2">
              <Button variant="secondary" onClick={() => setSwitchModalOpen(true)}>
                {t('subscription:currentPlan.switchPlan')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Invoices */}
      <Card title={t('subscription:invoices.title')} noPadding>
        {loading && invoices.length === 0 ? (
          <p className="text-[13px] text-slate-500 text-center py-6">{t('common:actions.loading')}</p>
        ) : invoices.length === 0 ? (
          <EmptyState title={t('subscription:invoices.empty')} />
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('subscription:invoices.fields.number')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">
                  {t('subscription:invoices.fields.period')}
                </TableHeader>
                <TableHeader className="text-right">{t('subscription:invoices.fields.amount')}</TableHeader>
                <TableHeader>{t('subscription:invoices.fields.status')}</TableHeader>
                <TableHeader className="text-right">
                  <span className="sr-only">PDF</span>
                </TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv) => {
                const overdue = isOverdue(inv, today);
                const isCredit = (inv.netAmount ?? 0) < 0;
                const amount = inv.grossAmount ?? inv.netAmount ?? 0;
                return (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium text-slate-900 tabular-nums">
                      {inv.invoiceNumber ?? '—'}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-slate-600 whitespace-nowrap tabular-nums">
                      {inv.periodStart ? formatDate(inv.periodStart) : '—'}
                      {' – '}
                      {inv.periodEnd ? formatDate(inv.periodEnd) : '—'}
                    </TableCell>
                    <TableCell
                      className={`text-right tabular-nums ${isCredit ? 'text-red-600' : 'text-slate-900'}`}
                    >
                      {formatMoney(amount, { currency: inv.currency })}
                    </TableCell>
                    <TableCell>
                      <Badge color={statusColor(inv, overdue)}>
                        {t(`subscription:invoices.status.${statusKey(inv, overdue)}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => inv.id && downloadPdf(inv.id, inv.invoiceNumber)}
                        disabled={downloadingPdf || !inv.id}
                        title={t('subscription:invoices.actions.downloadPdf')}
                        leftIcon={<Download className="w-3.5 h-3.5" strokeWidth={2} />}
                      >
                        PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <MeSupportCreateModal
        isOpen={switchModalOpen}
        onClose={() => setSwitchModalOpen(false)}
        prefill={{
          type: SupportTicketType.Question,
          subject: t('subscription:currentPlan.switchPlanSubject'),
          message:
            t('subscription:currentPlan.switchPlanMessageTemplate', {
              currentPlan: plan?.name ?? '',
            }) ?? '',
        }}
      />
    </div>
  );
}
